import { createContext, useContext, useMemo, useState } from "react";

import Service from "../../api/service";

const BaseLevelContext = createContext(null);

export const useBaseLevelContext = () => {
  const v = useContext(BaseLevelContext);
  if (!v) {
    throw new Error(
      "useBaseLevelContext must be used inside BaseLevelProvider",
    );
  }
  return v;
};

export const BaseLevelProvider = ({ children }) => {
  const [count, setCount] = useState(0);
  const [allLevels, setAllLevels] = useState(null);
  const [singleLevel, setSingleLevel] = useState(null);
  const [temporaryLevel, setTemporaryLevel] = useState(null);
  const [temporaryLevelName, setTemporaryLevelName] = useState(null);

  const fetchLevels = async ({ level, page = 1, pageSize = 50 }) => {
    const response = await Service.get(
      `/api/v1/${level}/level/?page=${page}&pageSize=${pageSize}`,
    );
    const payload = response?.data;
    if (payload) {
      setAllLevels(payload.data);
      setCount(payload.count);
    }
    return response;
  };

  const fetchBaseByCountryId = async ({
    level,
    country_id,
    page = 1,
    pageSize = 20,
  }) => {
    const response = await Service.get(
      `/api/v1/${level}/level/location/?page=${page}&pageSize=${pageSize}&country_id=${country_id}`,
    );
    const payload = response?.data;
    if (payload) {
      setAllLevels(payload.data);
      setCount(payload.count);
    }
    return response;
  };

  // Non-stateful variant — returns payload directly.
  const getLevelsByCountryId = async ({
    level,
    country_id,
    page = 1,
    pageSize = 20,
  }) => {
    const { data, error } = await Service.get(
      `/api/v1/${level}/level/location/?page=${page}&pageSize=${pageSize}&country_id=${country_id}`,
    );
    return { data: data?.data, error };
  };

  const fetchOneLevel = async ({ level, level_id }) => {
    const response = await Service.get(
      `/api/v1/${level}/level/metadata/${level_id}`,
    );
    if (response?.data !== undefined) setSingleLevel(response.data);
    return response;
  };

  const create = async ({ level, data }) =>
    Service.post(`/api/v1/admin/${level}/level/create`, data);

  const update = async ({ level, level_id, data }) =>
    Service.put(`/api/v1/admin/${level}/level/update/${level_id}`, data);

  const deleteOne = async ({ level, level_id }) =>
    Service.delete(`/api/v1/admin/${level}/level/delete/${level_id}`);

  const value = useMemo(
    () => ({
      count,
      allLevels,
      singleLevel,
      temporaryLevel,
      temporaryLevelName,
      setAllLevels,
      setTemporaryLevel,
      setTemporaryLevelName,
      fetchLevels,
      fetchBaseByCountryId,
      getLevelsByCountryId,
      fetchOneLevel,
      create,
      update,
      deleteOne,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [count, allLevels, singleLevel, temporaryLevel, temporaryLevelName],
  );

  return (
    <BaseLevelContext.Provider value={value}>
      {children}
    </BaseLevelContext.Provider>
  );
};
